import React from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  Box,
  Button,
  Dialog,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import { MyTimesheetShowRequestDialogHeader } from "my-timesheet/components";
import { OcDatePicker, OcFormSwitch } from "oc/components";
import { chartColorPalette } from "oc/themes/colors";
import { getDateFormatByLocale } from "oc/utils";
import * as Yup from "yup";

const useStyles = makeStyles((theme: any) => ({
  formControl: {
    minWidth: 120,
    width: "100%",
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

const getValidationSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    name: Yup.string().required(t("OC_NAME_REQUIRED")),
    description: Yup.string(),
    validFrom: Yup.date().required(t("OC_START_DATE_REQUIRED")),
    validTo: Yup.date()
      .nullable()
      .test({
        name: "validTo",
        exclusive: false,
        params: {},
        message: t("OC_END_DATE_BEFORE_START_DATE"),
        test: function (value) {
          if (value) {
            return value >= this.parent.validFrom;
          }
          return true;
        },
      }),
  });

const getCostValidationSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    name: Yup.string().required(t("OC_NAME_REQUIRED")),
    description: Yup.string(),
    value: Yup.number()
      .transform((val, orig) => (orig === "" ? undefined : val))
      .typeError(t("OC_INCORRECT_FORMAT"))
      .required(t("MY_TIMESHEET_COST_VALUE_REQUIRED"))
      .min(0, t("OC_INCORRECT_FORMAT")),
    billable: Yup.boolean(),
    validFrom: Yup.date().required(t("OC_START_DATE_REQUIRED")),
    validTo: Yup.date()
      .nullable()
      .test({
        name: "validTo",
        exclusive: false,
        params: {},
        message: t("OC_END_DATE_BEFORE_START_DATE"),
        test: function (value) {
          if (value) {
            return value >= this.parent.validFrom;
          }
          return true;
        },
      }),
  });

interface MyTimesheetEntityEditorProps {
  open: boolean;
  onClose: () => void;
  entity: string;
  entityData: any;
  saveEntity: (values: any) => void;
  dialogLabel?: string;
  isMobile: boolean;
}

export const MyTimesheetEntityEditor: React.FC<
  MyTimesheetEntityEditorProps
> = ({
  open,
  onClose,
  entity,
  entityData,
  saveEntity,
  dialogLabel = "",
  isMobile,
}) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const isCostEntity = entity === "OC_COST";
    const isEditEntity = Boolean(entityData?._id);

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: entityData,
      validationSchema: isCostEntity
        ? getCostValidationSchema(t)
        : getValidationSchema(t),
      onSubmit: async (values) => {
        saveEntity(values);
      },
    });

    const colors = (
      Object.keys(chartColorPalette) as Array<keyof typeof chartColorPalette>
    ).map((color) => chartColorPalette[color]);

    const handleCloseDialog = () => {
      onClose();
      formik.resetForm();
    };

    const handleValidityChange = (field: string, date: Date | null) => {
      formik.setFieldValue(field, date);
    };

    const disableSaveButton = _.isEqual(entityData, formik.values);

    const handleChange = (field: string, value: any) => {
      formik.setFieldValue(field, value);
    };

    const label =
      entity === "OC_COST"
        ? t("MY_TIMESHEET_COST_NAME")
        : t("MY_TIMESHEET_CLIENT_NAME");

    return (
      <Dialog open={open} fullScreen={isMobile}>
        <Box padding="0px 16px 16px 16px">
          <MyTimesheetShowRequestDialogHeader
            label={t(dialogLabel)}
            close={onClose}
            isMobile={isMobile}
          />
          <Box
            style={{
              height: "2px",
              backgroundColor: "#C6CACC",
              marginBottom: "16px",
            }}
          />
          <Box height="16px" />
          <TextField
            id="name"
            name="name"
            label={label}
            variant="outlined"
            style={{ width: "100%" }}
            value={formik.values.name || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange("name", e.target.value)
            }
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={
              formik.touched.name && formik.errors.name
                ? String(formik.errors.name)
                : undefined
            }
          />
          {isEditEntity && (
            <>
              <Box height="32px" />
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel
                  id="color-select-label"
                  style={{
                    backgroundColor: "white",
                    padding: "0px 6px",
                  }}
                >
                  {t("MY_TIMESHEET_SELECT_COLOR")}
                </InputLabel>
                <Select
                  labelId="color-select-label"
                  id="color-select"
                  value={formik.values.color || ""}
                  onChange={(e: SelectChangeEvent) =>
                    handleChange("color", e.target.value)
                  }
                >
                  {colors.map((color) => (
                    <MenuItem value={color} key={color}>
                      <Box
                        width="24px"
                        height="24px"
                        style={{
                          backgroundColor: color,
                          borderRadius: "50%",
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {isCostEntity && (
            <>
              <Box height="32px" />
              <TextField
                id="value"
                name="value"
                label={t("OC_COST")}
                variant="outlined"
                style={{ width: "100%" }}
                value={formik.values.value ?? ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  formik.handleChange(e)
                }
                error={formik.touched.value && Boolean(formik.errors.value)}
                helperText={
                  formik.touched.value && formik.errors.value
                    ? String(formik.errors.value)
                    : undefined
                }
                InputProps={{
                  endAdornment: <InputAdornment position="end">Ft</InputAdornment>,
                }}
              />
              <Box height="32px" />
              <Box display="flex" flexDirection="row" alignItems="center">
                <OcFormSwitch
                  id="billable"
                  name="billable"
                  checked={formik.values.billable || false}
                  inputProps={{ "aria-label": "controlled" }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange("billable", e.target.checked)
                  }
                  disabled={false}
                />
                <Box width="16px" />
                <Typography variant="caption">{t("MY_TIMESHEET_BILLABLE_COST")}</Typography>
              </Box>
            </>
          )}
          <Box height="32px" />
          <TextField
            id="description"
            name="description"
            label={t("OC_NOTE")}
            multiline
            minRows={3}
            variant="outlined"
            style={{ width: "100%", height: "auto" }}
            value={formik.values.description || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              formik.handleChange(e)
            }
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={
              formik.touched.description && formik.errors.description
                ? String(formik.errors.description)
                : undefined
            }
          />
          <Box height="16px" />
          <OcDatePicker
            id="validFrom"
            label={t("OC_VALID_FROM")}
            value={
              formik.values.validFrom
                ? new Date(formik.values.validFrom)
                : null
            }
            onChange={(date) => handleValidityChange("validFrom", date)}
            format={getDateFormatByLocale(i18n.language)}
            error={formik.touched.validFrom && Boolean(formik.errors.validFrom)}
            helperText={
              formik.touched.validFrom && formik.errors.validFrom
                ? String(formik.errors.validFrom)
                : undefined
            }
          />
          <Box height="32px" />
          <OcDatePicker
            id="validTo"
            label={t("OC_VALID_TO")}
            value={
              formik.values.validTo ? new Date(formik.values.validTo) : null
            }
            onChange={(date) => handleValidityChange("validTo", date)}
            format={getDateFormatByLocale(i18n.language)}
            error={formik.touched.validTo && Boolean(formik.errors.validTo)}
            helperText={
              formik.touched.validTo && formik.errors.validTo
                ? String(formik.errors.validTo)
                : undefined
            }
          />
          <Box height="48px" />
          <Box
            style={{
              height: "2px",
              backgroundColor: "#C6CACC",
              marginBottom: "16px",
            }}
          />
          <Box
            display="flex"
            flexDirection={isMobile ? "column-reverse" : "row"}
            justifyContent="space-between"
            width={isMobile ? "100%" : "568px"}
          >
            <Button
              id="handleCloseDialog"
              onClick={handleCloseDialog}
              color="secondary"
              variant="text"
              style={{ width: isMobile ? "100%" : "calc(50% - 8px)" }}
            >
              {t("OC_CANCEL")}
            </Button>
            {isMobile && <Box height="16px" />}
            <Button
              id="handleSubmit"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              variant="contained"
              color="primary"
              disableElevation
              disabled={disableSaveButton}
              style={{ width: isMobile ? "100%" : "calc(50% - 8px)" }}
            >
              {isEditEntity ? t("OC_SAVE") : t("MY_TIMESHEET_CREATE")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    );
  };

export default MyTimesheetEntityEditor;
